import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { capacitorService } from '../services/capacitorService';
import { brandDark, brandLight } from '../theme/brandTokens';
import { isPortalAuthRoute } from '../utils/portalAuthRoutes';

type DarkMode = 'light' | 'dark' | 'system';

interface DarkModeContextType {
  darkMode: DarkMode;
  setDarkMode: (mode: DarkMode) => void;
  isDarkModeActive: boolean;
  /** Temporary DOM theme (e.g. system on login); does not change saved preference. */
  setThemeApplyOverride: (mode: DarkMode | null) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

function resolveIsDark(activeMode: DarkMode): boolean {
  return (
    activeMode === 'dark' ||
    (activeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );
}

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [darkMode, setDarkModeState] = useState<DarkMode>(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'light' || savedMode === 'dark' || savedMode === 'system') {
      return savedMode;
    }
    return 'system';
  });

  const [themeApplyOverride, setThemeApplyOverrideState] = useState<DarkMode | null>(null);

  const setDarkMode = (mode: DarkMode) => {
    setDarkModeState(mode);
    localStorage.setItem('darkMode', mode);
  };

  const setThemeApplyOverride = useCallback((mode: DarkMode | null) => {
    setThemeApplyOverrideState(mode);
  }, []);

  const appliedMode = themeApplyOverride ?? darkMode;

  const isDarkModeActive = resolveIsDark(appliedMode);

  useEffect(() => {
    const applyDarkMode = (isDark: boolean) => {
      if (isDark) {
        document.documentElement.classList.add('dark-mode');
        document.body.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
        document.body.classList.remove('dark-mode');
      }
      // Update keyboard style to match theme (iOS only)
      capacitorService.setKeyboardStyle(isDark);
      
      // Update native window background color for safe areas
      // Note: This requires the native code to listen for this event
      // For now, the native background is set at launch in AppDelegate.swift
      // To update dynamically, you'd need a custom Capacitor plugin
      if (capacitorService.isNativeApp()) {
        // Set meta theme-color for webview background
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', isDark ? brandDark.paper : brandLight.paper);
        }
      }
    };

    if (appliedMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyDarkMode(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => applyDarkMode(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      applyDarkMode(appliedMode === 'dark');
    }
  }, [appliedMode]);

  return (
    <DarkModeContext.Provider value={{ darkMode, setDarkMode, isDarkModeActive, setThemeApplyOverride }}>
      {children}
    </DarkModeContext.Provider>
  );
};

/** Portal login / password flows: match OS theme; restore saved preference after sign-in. */
export const PortalAuthScreenThemeSync: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
  const { pathname } = useLocation();
  const { setThemeApplyOverride } = useDarkMode();

  useEffect(() => {
    if (!isLoggedIn && isPortalAuthRoute(pathname)) {
      setThemeApplyOverride('system');
    } else {
      setThemeApplyOverride(null);
    }
  }, [isLoggedIn, pathname, setThemeApplyOverride]);

  return null;
};

export const useDarkMode = (): DarkModeContextType => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
}; 